import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  CircularProgress,
} from "@mui/material";
import { Reservation } from "../../types/reservation";
import { t } from "i18next";
import { useTheme as useMuiTheme } from "@mui/material";

interface DeleteReservationDialogProps {
  open: boolean;
  onClose: () => void;
  reservation: Reservation | null;
  onDeleteSingle: (reservationId: number) => Promise<void>;
  onDeleteRecurring: (groupId: string) => Promise<void>;
  onDeleteContinuous: (groupId: string) => Promise<void>;
}

export const DeleteReservationDialog: React.FC<
  DeleteReservationDialogProps
> = ({
  open,
  onClose,
  reservation,
  onDeleteSingle,
  onDeleteRecurring,
  onDeleteContinuous,
}) => {
  const [deleteOption, setDeleteOption] = useState<"single" | "all">("single");
  const [isDeleting, setIsDeleting] = useState(false);

  const muiTheme = useMuiTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";
  const themeColors = {
    cardBg: isDarkMode ? "#1e1e1e" : "#ffffff",
    border: isDarkMode ? "rgba(255,255,255,0.12)" : "#e2e8f0",
  };

  if (!reservation) return null;

  const isRecurring = !!reservation.recurrenceGroupId;
  const isContinuous = !!reservation.continuousGroupId;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (isContinuous) {
        // Continuous reservations always delete all
        await onDeleteContinuous(reservation.continuousGroupId!);
      } else if (isRecurring) {
        if (deleteOption === "all") {
          await onDeleteRecurring(reservation.recurrenceGroupId!);
        } else {
          await onDeleteSingle(reservation.id);
        }
      } else {
        // Single reservation
        await onDeleteSingle(reservation.id);
      }
      onClose();
    } catch (error) {
      console.error("Failed to delete reservation:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getDialogContent = () => {
    if (isContinuous) {
      return (
        <Typography>
          {t("reservations.multiDayContinuousDeleteWarning.part1")}{" "}
          <strong>{t("reservations.multiDayContinuousReservation")}</strong>.{" "}
          {t("reservations.multiDayContinuousDeleteWarning.part2")}
        </Typography>
      );
    }

    if (isRecurring) {
      return (
        <Box>
          <Typography sx={{ mb: 2 }}>
            {t("reservations.recurringDeletePrompt.part1")}{" "}
            <strong>{t("reservations.recurringSeries")}</strong>.{" "}
            {t("reservations.recurringDeletePrompt.part2")}
          </Typography>
          <RadioGroup
            value={deleteOption}
            onChange={(e) =>
              setDeleteOption(e.target.value as "single" | "all")
            }
          >
            <FormControlLabel
              value="single"
              control={<Radio />}
              label={t("reservations.deleteSingleInstance")}
            />
            <FormControlLabel
              value="all"
              control={<Radio />}
              label={t("reservations.deleteAllRecurringInstances")}
            />
          </RadioGroup>
        </Box>
      );
    }

    return <Typography>{t("reservations.singleDeletePrompt")}</Typography>;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: themeColors.cardBg,
          borderBottom: `1px solid ${themeColors.border}`,
          py: 2,
        }}
      >
        {t("reservations.deleteReservation")}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box
          sx={{
            gap: 2,
            mt: 3,
          }}
        >
          {getDialogContent()}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button onClick={onClose} disabled={isDeleting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} /> : null}
        >
          {isDeleting ? t("reservations.deleting") : t("common.delete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteReservationDialog;
